/**
 * <s-form-validation> wraps a form's fields and replaces the browser's
 * validation bubble with an inline message under each invalid field.
 * Fields keep native `required` / `type` rules, so the form still cannot be
 * submitted until they pass. Each field supplies its message in
 * `data-error-message` and names its message element in `data-error-target`.
 */
class SFormValidation extends HTMLElement {
  connectedCallback() {
    this.form = this.closest('form');
    if (!this.form) return;
    this.firstInvalid = null;

    this.onInvalid = (event) => {
      const field = event.target;
      if (!(field instanceof HTMLElement) || !field.dataset.errorTarget) return;
      event.preventDefault();
      this.show(field);
      if (!this.firstInvalid) {
        this.firstInvalid = field;
        field.focus({ preventScroll: true });
        field.scrollIntoView({ block: 'center', behavior: 'smooth' });
        queueMicrotask(() => (this.firstInvalid = null));
      }
    };
    this.onInput = (event) => {
      const field = event.target;
      if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) {
        if (field.dataset.errorTarget && field.checkValidity()) this.clear(field);
      }
    };
    this.onSubmit = () => (this.firstInvalid = null);

    this.form.addEventListener('invalid', this.onInvalid, true);
    this.form.addEventListener('input', this.onInput);
    this.form.addEventListener('submit', this.onSubmit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('invalid', this.onInvalid, true);
    this.form?.removeEventListener('input', this.onInput);
    this.form?.removeEventListener('submit', this.onSubmit);
  }

  show(field) {
    const message = document.getElementById(field.dataset.errorTarget);
    if (!message) return;
    message.textContent = field.dataset.errorMessage || field.validationMessage;
    message.hidden = false;
    field.setAttribute('aria-invalid', 'true');
    field.setAttribute('aria-describedby', message.id);
  }

  clear(field) {
    const message = document.getElementById(field.dataset.errorTarget);
    if (message) {
      message.hidden = true;
      message.textContent = '';
    }
    field.removeAttribute('aria-invalid');
  }
}

if (!customElements.get('s-form-validation')) {
  customElements.define('s-form-validation', SFormValidation);
}
